import React from 'react';
import { Heart } from 'lucide-react';
import { useCart } from '../../../contexts/CartContext';
import { CART_CONSTANTS } from '../FuncationsCartAndWishlist/Dto/AddToCardtUtilityDto';
import './styles/ProductCard.css';

interface ProductCardProps {
  id: string;
  title: string;
  price: string;
  image: string;
  slug: string;
  deliveryTag?: boolean;
  onAddToCart?: () => void;
  onMoreInfo?: () => void;
  onAddToWishlist?: () => void;
  onViewOptions?: () => void;
  isWishlisted?: boolean;
  quantity?: number;
  variants?: any[];
  hierarchicalVariants?: any[];
  variantHierarchy?: string[];
}

const ProductCard: React.FC<ProductCardProps> = ({
  id,
  title,
  price,
  image,
  deliveryTag = true,
  onAddToCart,
  onMoreInfo,
  onAddToWishlist,
  onViewOptions,
  isWishlisted = false,
  quantity,
  variants}) => {
  const { cartItems, updateQuantity, removeFromCart } = useCart();

  const cartItem = cartItems.find(item => {
    if (item.productId !== id) return false;
    return !item.variantSku || item.variantSku === '';
  });

  const isInCart = !!cartItem;
  const cartQuantity = cartItem?.quantity || 0;
  const handleWishlistClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddToWishlist) {
      onAddToWishlist();
    }
  };

  const handleCartQuantityChange = (newQuantity: number) => {
    if (!cartItem) return;

    if (newQuantity <= 0) {
      removeFromCart(cartItem.productId, cartItem.variantSku, cartItem.variantId);
    } else {
      updateQuantity(cartItem.productId, newQuantity, cartItem.variantSku, cartItem.variantId);
    }
  };

  const handleDecreaseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    handleCartQuantityChange(cartQuantity - 1);
  };

  const handleIncreaseClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (cartQuantity < CART_CONSTANTS.MAX_QUANTITY) {
      handleCartQuantityChange(cartQuantity + 1);
    }
  };

  const handleAddToCartClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddToCart) {
      onAddToCart();
    }
  };

  const handleViewOptionsClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onViewOptions) {
      onViewOptions();
    }
  };

  const handleCardClick = () => {
    if (onMoreInfo) {
      onMoreInfo();
    }
  };

  const outOfStock = typeof quantity === 'number' ? quantity === 0 : false;
  return (
    <div 
      className="product-card" 
      onClick={handleCardClick} 
      style={{ cursor: 'pointer', position: 'relative' }}
    >
      {/* Out of Stock Badge */}
      {outOfStock && (
        <div style={{position: 'absolute', top: 8, left: 8, background: '#ccc', color: '#fff', padding: '2px 8px', borderRadius: '8px', fontSize: '0.8rem', zIndex: 20}}>
          Out of Stock
        </div>
      )}
      {/* Wishlist Icon - No navigation on click */}
      <div 
        className="wishlist-icon" 
        title={isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'} 
        onClick={handleWishlistClick}
        role="button" 
        aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
        style={{ cursor: 'pointer', position: 'absolute', top: '0.5rem', right: '0.5rem', zIndex: 10 }}
      >
        <Heart 
          size={22} 
          color={isWishlisted ? '#e0245e' : '#000000ff'} 
          strokeWidth={2.2} 
          fill={isWishlisted ? '#e0245e' : 'none'} 
        />
      </div>

      {/* Delivery Tag */}
      {deliveryTag && (
        <div className="delivery-tag">
          30 min Delivery
        </div>
      )}
      
      {/* Product Image */}
      <div className="product-image-container">
        <img 
          src={image} 
          alt={title}
          className="product-image"
        />
      </div>
      
      {/* Product Info */}
      <div className="product-info">
        <h3 className="product-title">{title}</h3>
        <div className="product-price-button-container">
          {!variants || variants.length === 0 ? <p className="product-price">{price}</p> : null}
          {/* Add to Cart / Quantity Controls / View Options */}
          {variants && variants.length > 0 ? (
            <button
              type="button"
              className="view-options-btn"
              onClick={handleViewOptionsClick}
              style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}
              aria-label="View options"
            >
              View Options
            </button>
          ) : isInCart ? (
            <div className="cart-quantity-controls">
              <button
                type="button"
                className="cart-quantity-btn"
                onClick={handleDecreaseClick}
                style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}
                aria-label="Decrease quantity"
              >
                -
              </button>
              <span className="cart-quantity-display">{cartQuantity}</span>
              <button
                type="button"
                className="cart-quantity-btn"
                onClick={handleIncreaseClick}
                style={{ cursor: 'pointer', zIndex: 10, position: 'relative' }}
                aria-label="Increase quantity"
                disabled={cartQuantity >= CART_CONSTANTS.MAX_QUANTITY}
              >
                +
              </button>
            </div>
          ) : (
            <button 
              type="button"
              className="product-add-to-cart-btn"
              onClick={handleAddToCartClick}
              style={outOfStock ? { backgroundColor: '#ccc', color: '#888', cursor: 'not-allowed', zIndex: 10, position: 'relative' } : { cursor: 'pointer', zIndex: 10, position: 'relative' }}
              aria-label="Add to cart"
              disabled={quantity === 0}
            >
              Add
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;